import React, { useState, useEffect } from 'react';
import { Calendar, Users, Image } from 'lucide-react';
import ApiService from '../../services/api';

interface Appointment {
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  service: string;
  date: string;
  time: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  notes?: string;
}

interface DashboardStats {
  totalAppointments: number;
  pendingAppointments: number;
  confirmedAppointments: number;
  completedAppointments: number;
  totalGalleryImages: number;
}

const Dashboard: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalAppointments: 0,
    pendingAppointments: 0,
    confirmedAppointments: 0,
    completedAppointments: 0,
    totalGalleryImages: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [homeSettings, setHomeSettings] = useState<any>(null);

  // Dashboard verilerini yükle
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getDashboardStats();
      setDashboardStats(response.stats);
      if (response.recentAppointments) {
        setAppointments(response.recentAppointments);
      }
    } catch (error: any) {
      console.error('Dashboard verileri yüklenirken hata:', error);
      setError('Dashboard verileri yüklenemedi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Home settings yükle
  const loadHomeSettings = async () => {
    try {
      const response = await ApiService.getHomeSettings();
      setHomeSettings(response.settings);
    } catch (error) {
      console.error('Home settings yüklenemedi:', error);
    }
  };

  useEffect(() => {
    loadDashboardData();
    loadHomeSettings();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-900 text-green-200';
      case 'pending': return 'bg-yellow-900 text-yellow-200';
      case 'completed': return 'bg-blue-900 text-blue-200';
      case 'cancelled': return 'bg-red-900 text-red-200';
      default: return 'bg-gray-900 text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-white">
      <h2 className="text-2xl font-bold text-white">Dashboard</h2>
      
      {error && (
        <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="p-6 rounded-lg shadow-sm border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-300">Toplam Randevu</p>
              <p className="text-2xl font-bold text-white">{dashboardStats.totalAppointments}</p>
            </div>
            <Calendar className="w-8 h-8 text-pink-500" />
          </div>
        </div>
        
        <div className="p-6 rounded-lg shadow-sm border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-300">Bekleyen</p>
              <p className="text-2xl font-bold text-yellow-400">{dashboardStats.pendingAppointments}</p>
            </div>
            <Users className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        
        <div className="p-6 rounded-lg shadow-sm border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-300">Onaylanan</p>
              <p className="text-2xl font-bold text-green-400">{dashboardStats.confirmedAppointments}</p>
            </div>
            <Calendar className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="p-6 rounded-lg shadow-sm border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-300">Galeri Fotoğrafları</p>
              <p className="text-2xl font-bold text-purple-400">{dashboardStats.totalGalleryImages}</p>
            </div>
            <Image className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Recent Appointments */}
      <div className="rounded-lg shadow-sm p-6 border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <h3 className="text-lg font-semibold text-white mb-4">Son Randevular</h3>
        <div className="space-y-3">
          {appointments.slice(0, 3).map((appointment) => (
            <div key={appointment.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg space-y-2 sm:space-y-0" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
              <div>
                <p className="font-medium text-white">{appointment.customerName}</p>
                <p className="text-sm text-gray-300">{appointment.service} - {appointment.date} {appointment.time}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium self-start sm:self-center ${getStatusColor(appointment.status)}`}>
                {appointment.status === 'pending' ? 'Bekliyor' : 
                 appointment.status === 'confirmed' ? 'Onaylandı' :
                 appointment.status === 'completed' ? 'Tamamlandı' : 'İptal'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;